import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Autoplay,
  Controller,
  EffectCoverflow,
  Pagination,
} from "swiper/modules";
import { renderIcon } from "../../utils/iconUtils";
import websiteData from "../../data/websiteData.json";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-coverflow";
import "swiper/css/effect-fade";
import "swiper/css/mousewheel";
import "swiper/css/controller";

const AchievementSlider: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [imagesSwiper, setImagesSwiper] = useState<any>(null);
  const [numbersSwiper, setNumbersSwiper] = useState<any>(null);
  const [titlesSwiper, setTitlesSwiper] = useState<any>(null);

  const achievements = websiteData.achievements.achievements;

  // Handle slide change for all swipers
  const handleSlideChange = (swiper: any) => {
    setActiveIndex(swiper?.realIndex ?? 0);
  };

  // Handle pagination click
  const handlePaginationClick = (index: number) => {
    if (imagesSwiper) {
      imagesSwiper.slideTo(index);
    }
  };

  return (
    <div className="py-16 relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12 relative z-10">
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 bg-clip-text text-transparent day-theme:from-purple-600 day-theme:via-pink-600 day-theme:to-red-600">
            {websiteData.achievements.title}
          </h2>
          <p className="text-lg text-white/85 max-w-2xl mx-auto day-theme:text-slate-700">
            {websiteData.achievements.subtitle}
          </p>
          <div className="w-16 h-1 bg-gradient-to-r from-purple-400 to-pink-400 mx-auto mt-6 rounded-full"></div>
        </div>

        {/* Sliders Container */}
        <div className="relative inline-flex w-full items-center justify-center">
          {/* Numbers Slider (Background) */}
          <div className="absolute left-0 top-0 z-20 pointer-events-none [@supports(anchor-name:--test)]:left-[anchor(--achievement-image_left)] [@supports(anchor-name:--test)]:top-[anchor(--achievement-image_top)] [@supports(anchor-name:--test)]:-translate-x-1/4 [@supports(anchor-name:--test)]:-translate-y-1/4">
            <Swiper
              onSwiper={setNumbersSwiper}
              slidesPerView={1}
              direction="vertical"
              spaceBetween={0}
              loop={true}
              className="w-60 h-60"
              modules={[Controller]}
            >
              {achievements.map((_, index) => (
                <SwiperSlide
                  key={index}
                  className="flex items-center justify-center peer-hover:scale-110 transition-transform duration-500"
                >
                  <div className="text-9xl font-black text-white/20 select-none transition-colors duration-500">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Images Slider (Main) */}
          <div className="relative z-10 group [anchor-name:--achievement-image]">
            <Swiper
              onSwiper={setImagesSwiper}
              onSlideChange={handleSlideChange}
              controller={{
                control:
                  !numbersSwiper || !titlesSwiper
                    ? []
                    : [numbersSwiper, titlesSwiper],
              }}
              slidesPerView={1}
              autoplay={false}
              spaceBetween={0}
              loop={true}
              effect="coverflow"
              coverflowEffect={{
                rotate: 0,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: false,
              }}
              pagination={{
                el: ".pagination-custom",
                horizontalClass: "h-1",
                bulletActiveClass: "bg-red-500",
                bulletClass:
                  "w-9 h-1 mr-2 last:mr-0 cursor-pointer inline-block bg-gray-600 rounded",
                enabled: true,
                clickable: true,
              }}
              className="w-[700px] h-[400px]"
              modules={[EffectCoverflow, Controller, Autoplay, Pagination]}
            >
              {achievements.map((achievement, index) => (
                <SwiperSlide
                  key={index}
                  className="flex items-center justify-center group"
                >
                  <div className="relative w-full h-full rounded-xl overflow-hidden">
                    <div
                      className="w-full h-full bg-cover bg-center animate-ken-burns peer"
                      style={{
                        backgroundImage: `url(${achievement.images[0]})`,
                      }}
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute inset-0 bg-black/20"></div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Titles Slider (Lower left corner) */}
          <div className="absolute left-8 bottom-8 z-20 pointer-events-none [@supports(anchor-name:--test)]:left-[anchor(--achievement-image_left)] [@supports(anchor-name:--test)]:bottom-[anchor(--achievement-image_bottom)] [@supports(anchor-name:--test)]:translate-x-8 [@supports(anchor-name:--test)]:-translate-y-2">
            <Swiper
              onSwiper={setTitlesSwiper}
              direction="vertical"
              slidesPerView={1}
              spaceBetween={0}
              loop={true}
              className="w-full h-32"
              modules={[Controller]}
            >
              {achievements.map((achievement, index) => (
                <SwiperSlide
                  key={index}
                  className="flex items-end justify-start peer-hover:translate-x-2 transition-transform duration-500"
                >
                  <div className="space-y-1">
                    <h3 className="text-3xl font-bold text-white text-shadow-lg leading-tight peer-hover:text-purple-300 transition-colors duration-500">
                      {achievement.title}
                    </h3>
                    <div className="flex items-center gap-3 text-sm text-white/80 text-shadow-md">
                      <div className="flex items-center gap-1">
                        <svg
                          className="w-3 h-3 text-purple-300"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="font-medium">{achievement.date}</span>
                      </div>
                      <div className="w-1 h-1 bg-white/60 rounded-full"></div>
                      <div className="flex items-center gap-1">
                        <svg
                          className="w-3 h-3 text-purple-300"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="font-medium">
                          {achievement.organization}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                        {renderIcon(achievement.icon, {
                          className: "text-white text-xs",
                        })}
                      </div>
                      <p className="text-sm text-white/70 text-shadow-md font-medium peer-hover:text-purple-200 transition-colors duration-500">
                        {achievement.category}
                      </p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Custom Pagination */}
          <div className="pagination-custom absolute z-20  [left:unset] [bottom:unset] [@supports(anchor-name:--test)]:right-[anchor(--achievement-image_right)] [@supports(anchor-name:--test)]:top-[anchor(--achievement-image_bottom)] [@supports(anchor-name:--test)]:translate-y-2">
            {/* <div className="flex space-x-2">
              {achievements.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handlePaginationClick(index)}
                  className="relative w-9 h-1 cursor-pointer focus:!outline-0"
                >
                  <div className="absolute inset-0 w-full h-0.5 bg-gray-600 rounded"></div>
                  <div
                    className={`absolute inset-0 w-full h-1 bg-red-500 rounded transition-opacity duration-500  ${
                      index === activeIndex ? "opacity-100" : "opacity-0"
                    }`}
                  ></div>
                </button>
              ))}
            </div> */}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={() => imagesSwiper?.slidePrev()}
            className="absolute left-6 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-all duration-300 z-30"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={() => imagesSwiper?.slideNext()}
            className="absolute right-6 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-all duration-300 z-30"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        {/* Achievement Details Card */}
        <div className="mt-8 max-w-3xl mx-auto">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 day-theme:bg-white/20">
            {/* Compact Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                {renderIcon(achievements[activeIndex].icon, {
                  className: "text-white text-lg",
                })}
              </div>
              <div>
                <h3 className="text-xl font-bold text-white day-theme:text-slate-900">
                  {achievements[activeIndex].title}
                </h3>
                <p className="text-purple-300 font-medium day-theme:text-purple-700 text-sm">
                  {achievements[activeIndex].category}
                </p>
              </div>
            </div>

            {/* Story Section */}
            <div className="mb-4">
              <h4 className="text-base font-semibold text-white day-theme:text-slate-900 mb-2">
                The Story Behind
              </h4>
              <p className="text-gray-200 leading-relaxed day-theme:text-slate-700 text-sm">
                {achievements[activeIndex].story}
              </p>
            </div>

            {/* Impact Section */}
            <div className="mb-4">
              <h4 className="text-base font-semibold text-white day-theme:text-slate-900 mb-2">
                Impact & Results
              </h4>
              <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-3 day-theme:from-purple-500/20 day-theme:to-pink-500/20 day-theme:border-purple-500/40">
                <p className="text-purple-200 font-medium day-theme:text-purple-800 text-sm">
                  {achievements[activeIndex].impact}
                </p>
              </div>
            </div>

            {/* Technologies Section */}
            <div>
              <h4 className="text-base font-semibold text-white day-theme:text-slate-900 mb-2">
                Technologies Used
              </h4>
              <div className="flex flex-wrap gap-2">
                {achievements[activeIndex].technologies.map((tech, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-200 rounded-full text-xs font-medium backdrop-blur-sm day-theme:from-purple-500/40 day-theme:to-pink-500/40 day-theme:border-purple-500/60 day-theme:text-slate-900"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementSlider;
